/**
 * `AgentDropdown` — picker for the Live Agent view.
 *
 * ADR-0159 §4 introduces the tri-state picker grouping (`VERIFIED` /
 * `IDENTIFIED` / `UNVERIFIED · BLOCKED PATH`) — the grouping is driven
 * by the tier resolved by `useAgentRegistry`, not by a client-authored
 * `verified: true` boolean. The `VERIFIED` group is honest: Phase 1 has
 * 0 members — Sill's registry carries no key material yet (key
 * verification is Phase 2), so no identity can be cryptographically
 * proven. The copy puts that gap on Sill, NOT on the vendors: some
 * agents (e.g. ChatGPT) do publish signing keys; Sill doesn't verify
 * them yet. The block still renders with a "0 members" placeholder for
 * shape.
 */

import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Bot,
  Check,
  ChevronsUpDown,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Skull,
} from 'lucide-react';

import { useC } from '../../../lib/theme.js';
import { useIsMobile } from '../../../lib/use-is-mobile.js';
import { AGENTS, findAgent, type VerificationTier } from '../roster.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface AgentDropdownProps {
  agentId: string;
  onChange: (id: string) => void;
  disabled: boolean;
  /**
   * Registry-backed tier resolver. Verified > identified > unknown /
   * blocked. Never defaults to `verified`.
   */
  tierFor: (rosterId: string) => VerificationTier;
}

/**
 * Picker group placement — driven by (a) the roster id's protocol
 * ('blocked' → BLOCKED PATH; 'custom' → SELF-BUILT), then (b) the
 * registry-backed tier ('verified' | 'identified' | 'unknown').
 *
 * MINOR 1 fix (ADR-0159 security review): 'custom' agents used to fall
 * into the IDENTIFIED group despite having no origin identity — the
 * roster is a merchant-supplied endpoint, not a registered vendor.
 * Give it its own SELF-BUILT · NO REGISTRY ENTRY group so the picker
 * caption stays truthful.
 */
function groupOf(
  agentId: string,
  tier: VerificationTier,
): 'verified' | 'identified' | 'self-built' | 'blocked' {
  if (agentId === 'unverified') return 'blocked';
  if (agentId === 'custom') return 'self-built';
  if (tier === 'verified') return 'verified';
  return 'identified';
}

export function AgentDropdown({ agentId, onChange, disabled, tierFor }: AgentDropdownProps): JSX.Element {
  const C = useC();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const agent = findAgent(agentId);
  const activeTier: VerificationTier = tierFor(agentId);
  const isBlocked = agent.protocol === 'blocked';

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const verifiedAgents = AGENTS.filter(
    (a) => groupOf(a.id, tierFor(a.id)) === 'verified',
  );
  const identifiedAgents = AGENTS.filter(
    (a) => groupOf(a.id, tierFor(a.id)) === 'identified',
  );
  const selfBuiltAgents = AGENTS.filter(
    (a) => groupOf(a.id, tierFor(a.id)) === 'self-built',
  );
  const blockedAgents = AGENTS.filter(
    (a) => groupOf(a.id, tierFor(a.id)) === 'blocked',
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className="w-full flex items-center gap-2 px-3"
        style={{
          height: 40,
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <div
          className="flex items-center justify-center flex-shrink-0"
          style={{
            width: 18,
            height: 18,
            background: isBlocked ? C.coralDim : agent.color,
            borderRadius: 2,
          }}
        >
          {isBlocked ? <Skull size={10} color={agent.color} /> : <Bot size={10} color="#FFFFFF" />}
        </div>
        <span
          className="flex-1 min-w-0 text-left"
          style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {agent.name}
        </span>
        {isBlocked ? (
          <ShieldAlert size={11} color={C.coralFg} className="flex-shrink-0" />
        ) : activeTier === 'verified' ? (
          <ShieldCheck size={11} color={C.limeFg} className="flex-shrink-0" />
        ) : (
          <Shield size={11} color={C.muted} className="flex-shrink-0" />
        )}
        <ChevronsUpDown size={12} color={C.muted} className="flex-shrink-0" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute top-full mt-1 z-30 max-h-72 overflow-y-auto"
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
              // On narrow viewports the panel outgrows its trigger so
              // the group headers don't wrap. Anchor LEFT and expand
              // RIGHT: since the single-row strip landed, the trigger's
              // right edge sits ~150px in from the viewport edge, so a
              // right-anchored near-viewport panel hung off-screen left
              // (founder repro 2026-07-05). The trigger's LEFT edge is
              // ~32-40px from the viewport, so left-anchored the panel
              // fits; capped at 360px so it never overflows right.
              left: 0,
              right: isMobile ? 'auto' : 0,
              minWidth: isMobile ? 'min(calc(100vw - 56px), 360px)' : undefined,
            }}
          >
            {/* VERIFIED group — rendered ONLY when it has members. An
                empty shelf with a roadmap explanation teaches nothing
                (founder feedback 2026-07-06); the group appears the day
                a registered agent carries verified keys. */}
            {verifiedAgents.length > 0 && (
              <>
                <div
                  className="px-3 py-2"
                  style={{ fontFamily: monoFont, fontSize: 9, color: C.limeFg, letterSpacing: 2, borderBottom: `1px solid ${C.borderSoft}` }}
                >
                  VERIFIED · IDENTITY PROVEN CRYPTOGRAPHICALLY
                </div>
                {verifiedAgents.map((a) => (
                <button
                  key={a.id}
                  onClick={() => {
                    onChange(a.id);
                    setOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left"
                  style={{ background: a.id === agentId ? C.surface2 : 'transparent', borderBottom: `1px solid ${C.borderSoft}` }}
                >
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: 18, height: 18, background: a.color, borderRadius: 2 }}>
                    <Bot size={9} color="#FFFFFF" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, fontWeight: 500 }}>{a.name}</div>
                    <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, marginTop: 1, letterSpacing: 0.5 }}>{a.org}</div>
                  </div>
                  {a.id === agentId && <Check size={11} color={C.limeFg} className="flex-shrink-0" />}
                  </button>
                ))}
              </>
            )}

            {/* IDENTIFIED group — every named agent sits here today. */}
            <div
              className="px-3 py-2"
              style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 2, borderBottom: `1px solid ${C.borderSoft}`, borderTop: `1px solid ${C.borderSoft}` }}
            >
              IDENTIFIED · KNOWN, NOT PROVEN
            </div>
            {identifiedAgents.map((a) => (
              <button
                key={a.id}
                onClick={() => {
                  onChange(a.id);
                  setOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 text-left"
                style={{ background: a.id === agentId ? C.surface2 : 'transparent', borderBottom: `1px solid ${C.borderSoft}` }}
              >
                <div className="flex items-center justify-center flex-shrink-0" style={{ width: 18, height: 18, background: a.color, borderRadius: 2 }}>
                  <Bot size={9} color="#FFFFFF" />
                </div>
                <div className="flex-1 min-w-0">
                  <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, fontWeight: 500 }}>{a.name}</div>
                  <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, marginTop: 1, letterSpacing: 0.5 }}>{a.org}</div>
                </div>
                {a.id === agentId && <Check size={11} color={C.limeFg} className="flex-shrink-0" />}
              </button>
            ))}

            {/* SELF-BUILT group — merchant-supplied endpoints have no
                origin identity by design (ADR-0159 MINOR 1 fix). */}
            {selfBuiltAgents.length > 0 && (
              <>
                <div
                  className="px-3 py-2"
                  style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 2, borderBottom: `1px solid ${C.borderSoft}`, borderTop: `1px solid ${C.borderSoft}` }}
                >
                  SELF-BUILT · NO REGISTRY ENTRY
                </div>
                {selfBuiltAgents.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => {
                      onChange(a.id);
                      setOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-left"
                    style={{ background: a.id === agentId ? C.surface2 : 'transparent', borderBottom: `1px solid ${C.borderSoft}` }}
                  >
                    <div className="flex items-center justify-center flex-shrink-0" style={{ width: 18, height: 18, background: a.color, borderRadius: 2 }}>
                      <Bot size={9} color="#FFFFFF" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, fontWeight: 500 }}>{a.name}</div>
                      <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, marginTop: 1, letterSpacing: 0.5 }}>{a.org}</div>
                    </div>
                    {a.id === agentId && <Check size={11} color={C.limeFg} className="flex-shrink-0" />}
                  </button>
                ))}
              </>
            )}

            {/* UNVERIFIED / BLOCKED PATH group */}
            <div
              className="px-3 py-2"
              style={{ fontFamily: monoFont, fontSize: 9, color: C.coralFg, letterSpacing: 2, borderBottom: `1px solid ${C.borderSoft}`, borderTop: `1px solid ${C.borderSoft}` }}
            >
              UNVERIFIED · BLOCKED PATH
            </div>
            {blockedAgents.map((a) => (
              <button
                key={a.id}
                onClick={() => {
                  onChange(a.id);
                  setOpen(false);
                }}
                className="w-full flex items-center gap-3 px-3 py-2 text-left"
                style={{ background: a.id === agentId ? C.surface2 : 'transparent' }}
              >
                <div className="flex items-center justify-center flex-shrink-0" style={{ width: 18, height: 18, background: C.coralDim, borderRadius: 2 }}>
                  <Skull size={9} color={a.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div style={{ fontFamily: sansFont, fontSize: 12, color: C.text, fontWeight: 500 }}>{a.name}</div>
                  <div style={{ fontFamily: monoFont, fontSize: 9, color: C.muted, marginTop: 1, letterSpacing: 0.5 }}>{a.org} · no signature</div>
                </div>
                {a.id === agentId && <Check size={11} color={C.limeFg} className="flex-shrink-0" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
